import { isActiveGroup } from 'sonata-static-storage/lib/helpers/itemsGrouping.helpers';

import { toId } from './groupIds';

import type { GroupCapabilities, GroupFamily, GroupLike, GroupNameError } from './grouping.types';

const VISIBLE_SCREEN_GROUP_TYPES = new Set(['custom', 'cartography']);

const screenGroupType = (group: GroupLike): string => group.groupType ?? 'custom';

/** Whether a group may appear anywhere in the UI (lists, filters, menus, dropdowns, tags). */
export function isVisibleGroup(family: GroupFamily, group: GroupLike): boolean {
  if (!isActiveGroup(group)) {
    return false;
  }
  return family !== 'screens' || VISIBLE_SCREEN_GROUP_TYPES.has(screenGroupType(group));
}

/** Whether the group is the system-managed "UnGrouped" one (screens only, never user-manageable). */
export function isDefaultGroup(family: GroupFamily, group: GroupLike): boolean {
  return family === 'screens' && isActiveGroup(group) && group.groupType === 'default';
}

/** Whether a group may be offered in the list filters: the visible ones plus the screens default group. */
export function isFilterableGroup(family: GroupFamily, group: GroupLike): boolean {
  return isVisibleGroup(family, group) || isDefaultGroup(family, group);
}

const NO_CAPABILITIES: GroupCapabilities = {
  canDelete: false,
  canEditMembers: false,
  canRecolor: false,
  canRename: false
};

/** What the user may do with a group: custom groups allow everything, cartographic ones only recolor and delete. */
export function getGroupCapabilities(family: GroupFamily, group: GroupLike): GroupCapabilities {
  if (!isVisibleGroup(family, group)) {
    return NO_CAPABILITIES;
  }
  const isCustom = group.groupType !== 'cartography';
  return { canDelete: true, canEditMembers: isCustom, canRecolor: true, canRename: isCustom };
}

/** Whether a visible group can be edited at all (see {@link getGroupCapabilities} for what exactly). */
export function isEditableGroup(family: GroupFamily, group: GroupLike): boolean {
  const { canDelete, canEditMembers, canRecolor, canRename } = getGroupCapabilities(family, group);
  return canDelete || canEditMembers || canRecolor || canRename;
}

/** Name must be non-empty and unique (case-insensitive, trimmed) among the family's non-deleted groups. */
export function validateGroupName(name: string, groups: GroupLike[], excludeGroupId?: string): GroupNameError | null {
  const normalized = name.trim().toLowerCase();
  if (!normalized) {
    return 'empty';
  }
  const isDuplicate = groups.some(
    group =>
      isActiveGroup(group) &&
      toId(group._id) !== excludeGroupId &&
      (group.name ?? '').trim().toLowerCase() === normalized
  );
  return isDuplicate ? 'duplicate' : null;
}

export interface NewGroupDraft {
  color: string;
  groups: GroupLike[];
  name: string;
  selectedItemIds: string[];
}

/** "New group" confirm: valid unique name, a color and at least one selected item. */
export function canConfirmNewGroup({ color, groups, name, selectedItemIds }: NewGroupDraft): boolean {
  return Boolean(color) && selectedItemIds.length > 0 && validateGroupName(name, groups) === null;
}

export interface AddToGroupDraft {
  groupId: string | null;
  memberIds: string[];
  selectedItemIds: string[];
}

/** "Add to existing group" confirm: a group explicitly picked and something new to add to it. */
export function canConfirmAddToGroup({ groupId, memberIds, selectedItemIds }: AddToGroupDraft): boolean {
  if (!groupId) {
    return false;
  }
  const members = new Set(memberIds);
  return selectedItemIds.some(id => !members.has(id));
}

export interface GroupEditValues {
  color: string;
  memberIds: string[];
  name: string;
}

export interface GroupEditDraft {
  draft: GroupEditValues & { deleteGroup?: boolean };
  groupId: string;
  groups: GroupLike[];
  original: GroupEditValues;
}

const sameMembers = (a: string[], b: string[]): boolean => {
  const setA = new Set(a);
  const setB = new Set(b);
  return setA.size === setB.size && [...setA].every(id => setB.has(id));
};

/** "Save changes" in Edit Groups: something changed and the new name, if renamed, is valid. */
export function canSaveGroupEdit({ draft, groupId, groups, original }: GroupEditDraft): boolean {
  if (draft.deleteGroup) {
    return true;
  }
  const renamed = draft.name.trim() !== original.name.trim();
  const changed = renamed || draft.color !== original.color || !sameMembers(draft.memberIds, original.memberIds);
  return changed && (!renamed || validateGroupName(draft.name, groups, groupId) === null);
}

export interface GroupActionsAvailability {
  addToGroup: boolean;
  editGroups: boolean;
  newGroup: boolean;
}

/** Bulk-menu group entries, same rules for every family: "Edit groups" is the only one not needing a selection. */
export function getGroupActionsAvailability({
  addableGroupCount,
  editableGroupCount,
  selectedCount
}: {
  /** Groups accepting new members (defaults to `editableGroupCount`). */
  addableGroupCount?: number;
  editableGroupCount: number;
  selectedCount: number;
}): GroupActionsAvailability {
  const hasSelection = selectedCount > 0;
  const hasEditableGroups = editableGroupCount > 0;
  return {
    addToGroup: hasSelection && (addableGroupCount ?? editableGroupCount) > 0,
    editGroups: hasEditableGroups,
    newGroup: hasSelection
  };
}
